// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import type { AutoSortRequestModelType } from 'common/models/flow/AutoSortRequest';
import type { AutoSortResponseModelType } from 'common/models/flow/AutoSortResponse';
import type { GeocodedLocationModelType } from 'common/models/flow/GeocodedLocation';
import type { VersionedMapIdModelType } from 'common/models/flow/VersionedMapId';
import type { IntelliSortFeedbackModelType } from 'common/models/flow/IntelliSortFeedback';
import type { ZoneBasedRoutingMetadataModelType } from 'common/models/flow/ZoneBasedRoutingMetadata';

export type IntelliSortRequestModelType = {
    id: string,
    request: AutoSortRequestModelType,
    response: AutoSortResponseModelType,
    geocodedLocation: GeocodedLocationModelType,
    allowedRouteIds: Array<sting>,
    versionedMapIds: Array<VersionedMapIdModelType>,
    feedback: IntelliSortFeedbackModelType,
    responseSource: string,
    date: string,
    zoneBasedRoutingMetadata: ZoneBasedRoutingMetadataModelType
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class IntelliSortRequest extends ImmutableModel {
    static fromJS(json: IntelliSortRequestModelType): IntelliSortRequest {
        const state: Object = Object.assign({}, json);
        state.request = AutoSortRequest.fromJS(state.request);
        state.response = AutoSortResponse.fromJS(state.response);
        state.geocodedLocation = GeocodedLocation.fromJS(
            state.geocodedLocation
        );
        state.allowedRouteIds = Immutable.List(state.allowedRouteIds);
        state.versionedMapIds = Immutable.List(
            state.versionedMapIds
        ).map(item => VersionedMapId.fromJS(item));
        state.feedback = IntelliSortFeedback.fromJS(state.feedback);
        state.zoneBasedRoutingMetadata = ZoneBasedRoutingMetadata.fromJS(
            state.zoneBasedRoutingMetadata
        );
        return new this(Immutable.Map(state));
    }

    toJS(): IntelliSortRequestModelType {
        return {
            id: this.id,
            request: this.request.toJS(),
            response: this.response.toJS(),
            geocodedLocation: this.geocodedLocation.toJS(),
            allowedRouteIds: this.allowedRouteIds.toArray(),
            versionedMapIds: this.versionedMapIds
                .toArray()
                .map(item => item.toJS()),
            feedback: this.feedback.toJS(),
            responseSource: this.responseSource,
            date: this.date,
            zoneBasedRoutingMetadata: this.zoneBasedRoutingMetadata.toJS()
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get request(): AutoSortRequest {
        return this._state.get('request');
    }

    setRequest(request: AutoSortRequest): this {
        return this.clone(this._state.set('request', request));
    }

    get response(): AutoSortResponse {
        return this._state.get('response');
    }

    setResponse(response: AutoSortResponse): this {
        return this.clone(this._state.set('response', response));
    }

    get geocodedLocation(): GeocodedLocation {
        return this._state.get('geocodedLocation');
    }

    setGeocodedLocation(geocodedLocation: GeocodedLocation): this {
        return this.clone(
            this._state.set('geocodedLocation', geocodedLocation)
        );
    }

    get allowedRouteIds(): Immutable.List<sting> {
        return this._state.get('allowedRouteIds');
    }

    setAllowedRouteIds(allowedRouteIds: Immutable.List<sting>): this {
        return this.clone(this._state.set('allowedRouteIds', allowedRouteIds));
    }

    get versionedMapIds(): Immutable.List<VersionedMapId> {
        return this._state.get('versionedMapIds');
    }

    setVersionedMapIds(versionedMapIds: Immutable.List<VersionedMapId>): this {
        return this.clone(this._state.set('versionedMapIds', versionedMapIds));
    }

    get feedback(): IntelliSortFeedback {
        return this._state.get('feedback');
    }

    setFeedback(feedback: IntelliSortFeedback): this {
        return this.clone(this._state.set('feedback', feedback));
    }

    get responseSource(): string {
        return this._state.get('responseSource');
    }

    setResponseSource(responseSource: string): this {
        return this.clone(this._state.set('responseSource', responseSource));
    }

    get date(): string {
        return this._state.get('date');
    }

    setDate(date: string): this {
        return this.clone(this._state.set('date', date));
    }

    get zoneBasedRoutingMetadata(): ZoneBasedRoutingMetadata {
        return this._state.get('zoneBasedRoutingMetadata');
    }

    setZoneBasedRoutingMetadata(
        zoneBasedRoutingMetadata: ZoneBasedRoutingMetadata
    ): this {
        return this.clone(
            this._state.set(
                'zoneBasedRoutingMetadata',
                zoneBasedRoutingMetadata
            )
        );
    }
}
